import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, type SponsorshipQuote, type SponsorshipRow } from '@chicare/shared';

import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { AdminError } from './AdminError';

/**
 * Paid placement: the rate card, and the deals sold against it.
 *
 * ── THE LINE THIS SCREEN DOES NOT CROSS ─────────────────────────────────────
 *
 * A sponsorship buys a SHOP visibility in a labelled block. There is no
 * control here — and no setting anywhere — that moves a pharmacy up the offer
 * ladder for a medicine. That ladder is ordered by what the buyer pays, the
 * product page says "by price" in as many words, and a buyer paying more for
 * the same drug because a pharmacy paid ChiCare is the one thing this platform
 * cannot be.
 *
 * It is said on the screen rather than only in a commit message, because the
 * person selling placement is the one who will eventually be asked for it.
 */
export function SponsorshipsScreen() {
  const [tab, setTab] = useState<'deals' | 'rates'>('deals');
  const [selling, setSelling] = useState(false);

  if (selling) {
    return <SellPlacement onDone={() => setSelling(false)} />;
  }

  return (
    <div className="mx-auto w-full max-w-[1000px] px-4 py-6 xl:px-8">
      <h1 className="m-0 font-display text-[26px] font-bold tracking-[-0.02em]">Advertising</h1>
      <p className="m-0 mt-1 max-w-[64ch] text-[13px] leading-relaxed text-muted">
        Shops pay to be seen in a labelled block. It never changes the order of the prices a buyer
        is shown for a medicine — that stays cheapest first, and the product page says so.
      </p>

      <div className="mb-5 mt-3 flex gap-1 border-b border-line">
        {(
          [
            ['deals', 'Placements sold'],
            ['rates', 'Rate card'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            data-testid={`ads-tab-${key}`}
            className={cn(
              'min-h-11 border-b-2 bg-transparent px-3 text-[13.5px] font-semibold',
              tab === key ? 'border-blue text-ink' : 'border-transparent text-muted hover:text-ink',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'rates' ? <RateCard /> : <Deals onSell={() => setSelling(true)} />}
    </div>
  );
}

// ── The rate card ───────────────────────────────────────────────────────────

function RateCard() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);
  const [naira, setNaira] = useState('');

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin', 'sponsorship-rates'],
    queryFn: ({ signal }) => api.sponsorshipRates(signal),
  });

  const save = useMutation({
    mutationFn: (input: { placement: string; sector: string; unit: string }) =>
      api.setSponsorshipRate({
        ...input,
        // Typed in naira, stored in kobo. Nothing downstream divides by 100.
        price_kobo: Math.round(Number(naira || 0) * 100),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'sponsorship-rates'] });
      setEditing(null);
      setNaira('');
    },
  });

  if (isError) {
    return <AdminError title="We couldn't load the rates" error={error} onRetry={() => refetch()} />;
  }

  if (isLoading) {
    return <Skeleton className="h-[280px] w-full" radius="card" />;
  }

  const placements = data?.placements ?? [];
  const units = data?.units ?? [];
  const sectors = data?.sectors ?? [];

  return (
    <>
      <p className="m-0 mb-3 max-w-[64ch] text-[12.5px] leading-relaxed text-muted">
        Set once, here. The alternative is a salesperson typing a number into each deal, and six
        months later nobody can say what anybody was charged or why one pharmacy pays twice what
        its neighbour does. Every placement sold keeps the rate it was sold at, so changing a
        price here never re-prices a deal already agreed.
      </p>

      <div className="space-y-3">
        {placements.map((placement) => (
          <section
            key={placement.value}
            className="overflow-hidden rounded-card border border-line bg-white"
          >
            <div className="border-b border-line px-4 py-2.5">
              <p className="m-0 text-[13.5px] font-bold text-ink">
                {placement.label}
                <span className="ml-2 font-mono text-[10.5px] font-normal uppercase tracking-[0.08em] text-faint">
                  {placement.slots} slots
                </span>
              </p>
              <p className="m-0 mt-0.5 text-[11.5px] leading-snug text-muted">{placement.blurb}</p>
            </div>

            {sectors.map((sector) =>
              units.map((unit) => {
                const key = `${placement.value}:${sector}:${unit}`;
                const rate = (data?.rates ?? []).find(
                  (one) =>
                    one.placement === placement.value &&
                    one.sector === sector &&
                    one.unit === unit,
                );

                return (
                  <div
                    key={key}
                    className="flex min-h-11 flex-wrap items-center gap-x-3 gap-y-1 border-b border-line-2 px-4 py-2 last:border-b-0"
                  >
                    <span className="w-[108px] shrink-0 text-[12.5px] text-muted">
                      {sector === 'veterinary' ? 'Veterinary' : 'Pharmacy'}
                    </span>
                    <span className="w-[62px] shrink-0 font-mono text-[11.5px] uppercase tracking-[0.06em] text-faint">
                      per {unit}
                    </span>

                    {editing === key ? (
                      <span className="flex flex-1 flex-wrap items-center gap-2">
                        <input
                          type="number"
                          min={0}
                          step="0.01"
                          value={naira}
                          autoFocus
                          data-testid={`rate-input-${key}`}
                          onChange={(event) => setNaira(event.target.value)}
                          placeholder="50000"
                          className="min-h-11 w-[140px] rounded-btn border border-line bg-white px-2.5 font-mono text-[13px] text-ink outline-none focus:border-blue"
                        />
                        <Button
                          size="sm"
                          disabled={save.isPending}
                          onClick={() =>
                            save.mutate({ placement: placement.value, sector, unit })
                          }
                        >
                          {save.isPending ? 'Saving…' : 'Save'}
                        </Button>
                        <button
                          type="button"
                          onClick={() => setEditing(null)}
                          className="min-h-11 bg-transparent px-1 text-[12.5px] font-semibold text-muted"
                        >
                          Cancel
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        data-testid={`rate-${key}`}
                        onClick={() => {
                          setEditing(key);
                          setNaira(rate ? String(rate.price_kobo / 100) : '');
                        }}
                        className="min-h-11 flex-1 bg-transparent px-0 text-left font-mono text-[13px] font-bold tabular-nums text-ink hover:text-blue"
                      >
                        {rate ? rate.price_display : <span className="text-faint">Not priced</span>}
                      </button>
                    )}
                  </div>
                );
              }),
            )}
          </section>
        ))}
      </div>
    </>
  );
}

// ── The deals ───────────────────────────────────────────────────────────────

function Deals({ onSell }: { onSell: () => void }) {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin', 'sponsorships'],
    queryFn: ({ signal }) => api.sponsorships(signal),
  });

  if (isError) {
    return (
      <AdminError title="We couldn't load the placements" error={error} onRetry={() => refetch()} />
    );
  }

  const deals = data?.deals ?? [];

  return (
    <>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap gap-2.5">
          <Tile label="Running now" value={data ? String(data.live) : null} />
          <Tile label="Live value" value={data?.liveValueDisplay ?? null} mono />
          <Tile label="Sold this year" value={data?.soldThisYearDisplay ?? null} mono />
        </div>

        <Button onClick={onSell}>Sell a placement</Button>
      </div>

      {isLoading ? (
        <div className="space-y-1.5">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-[58px] w-full" radius="card" />
          ))}
        </div>
      ) : deals.length === 0 ? (
        <EmptyState
          title="Nothing sold yet"
          body="Price the placements on the rate card, then sell one. Nothing shows to a buyer until it is marked paid."
          action={<Button onClick={onSell}>Sell a placement</Button>}
        />
      ) : (
        <div className="overflow-hidden rounded-card border border-line bg-white">
          {deals.map((deal) => (
            <DealRow key={deal.reference} deal={deal} />
          ))}
        </div>
      )}
    </>
  );
}

function DealRow({ deal }: { deal: SponsorshipRow }) {
  const queryClient = useQueryClient();
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState('');

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'sponsorships'] });
    setCancelling(false);
    setReason('');
  };

  const activate = useMutation({
    mutationFn: () => api.activateSponsorship(deal.reference),
    onSuccess: refresh,
  });

  const cancel = useMutation({
    mutationFn: () => api.cancelSponsorship(deal.reference, reason.trim()),
    onSuccess: refresh,
  });

  const error = [activate.error, cancel.error].find(
    (one): one is ApiError => one instanceof ApiError,
  );

  return (
    <div
      data-testid={`deal-${deal.reference}`}
      className="border-b border-line px-3.5 py-2.5 last:border-b-0"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="min-w-0 flex-1 basis-[180px]">
          <span className="block truncate text-[13.5px] font-bold text-ink">{deal.pharmacy}</span>
          <span className="mt-0.5 block text-[12px] text-muted">
            {deal.placement_label}
            {deal.category ? ` · ${deal.category}` : ''} ·{' '}
            {deal.sector === 'veterinary' ? 'Veterinary' : 'Pharmacy'}
          </span>
        </span>

        <span className="shrink-0 text-[11.5px] text-muted lg:w-[150px]">
          {deal.starts_on} → {deal.ends_on}
        </span>

        <span className="shrink-0 font-mono text-[13px] font-bold tabular-nums text-ink lg:w-[100px] lg:text-right">
          {deal.total_display}
        </span>

        <span className="flex shrink-0 items-center lg:w-[124px]">
          {deal.status === 'cancelled' ? (
            <span className="rounded-pill bg-paper px-2 py-[3px] text-[11px] font-medium text-muted">
              Cancelled
            </span>
          ) : deal.is_live ? (
            <span className="rounded-pill bg-verified-tint px-2 py-[3px] text-[11px] font-bold text-verified-ink">
              Showing now
            </span>
          ) : deal.status === 'active' ? (
            <span className="rounded-pill bg-blue-tint/60 px-2 py-[3px] text-[11px] font-medium text-blue">
              Paid · not in date
            </span>
          ) : (
            <span className="rounded-pill bg-caution-tint px-2 py-[3px] text-[11px] font-bold text-caution-ink">
              Awaiting payment
            </span>
          )}
        </span>

        <span className="flex shrink-0 gap-1">
          {deal.status === 'draft' ? (
            <Button size="sm" disabled={activate.isPending} onClick={() => activate.mutate()}>
              {activate.isPending ? 'Marking…' : 'Mark paid'}
            </Button>
          ) : null}

          {deal.status !== 'cancelled' ? (
            <button
              type="button"
              onClick={() => setCancelling((one) => !one)}
              className="min-h-11 bg-transparent px-1 text-[12px] font-semibold text-muted hover:text-danger"
            >
              Cancel
            </button>
          ) : null}
        </span>
      </div>

      {cancelling ? (
        <div className="mt-2 border-t border-line pt-2">
          <input
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Why is this being cancelled?"
            className="min-h-11 w-full rounded-btn border border-line bg-white px-3 text-[13px] text-ink outline-none focus:border-blue"
          />
          <Button
            size="sm"
            variant="grey"
            className="mt-2"
            disabled={reason.trim().length < 10 || cancel.isPending}
            onClick={() => cancel.mutate()}
          >
            {cancel.isPending ? 'Cancelling…' : 'Cancel this placement'}
          </Button>
        </div>
      ) : null}

      {error ? (
        <p role="alert" className="m-0 mt-1.5 text-[12px] font-medium text-danger">
          {error.message}
        </p>
      ) : null}
    </div>
  );
}

// ── Selling one ─────────────────────────────────────────────────────────────

function SellPlacement({ onDone }: { onDone: () => void }) {
  const queryClient = useQueryClient();

  const [pharmacyId, setPharmacyId] = useState('');
  const [placement, setPlacement] = useState('home');
  const [sector, setSector] = useState('human');
  const [unit, setUnit] = useState('week');
  const [units, setUnits] = useState('4');
  const [startsOn, setStartsOn] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');
  const [quote, setQuote] = useState<SponsorshipQuote | null>(null);

  const rates = useQuery({
    queryKey: ['admin', 'sponsorship-rates'],
    queryFn: ({ signal }) => api.sponsorshipRates(signal),
  });

  const deal = { placement, sector, unit, units: Number(units || 0), starts_on: startsOn };

  const price = useMutation({
    mutationFn: () => api.quoteSponsorship(deal),
    onSuccess: setQuote,
  });

  const sell = useMutation({
    mutationFn: () =>
      api.sellSponsorship({
        ...deal,
        pharmacy_id: Number(pharmacyId),
        note: note.trim() || undefined,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'sponsorships'] });
      onDone();
    },
  });

  const error = [price.error, sell.error].find((one): one is ApiError => one instanceof ApiError);

  const change = (setter: (value: string) => void) => (value: string) => {
    setter(value);
    setQuote(null);
  };

  return (
    <div className="mx-auto w-full max-w-[600px] px-4 py-6 xl:px-8">
      <button
        type="button"
        onClick={onDone}
        className="mb-4 inline-flex min-h-11 items-center gap-1.5 bg-transparent p-0 text-[13px] font-semibold text-muted"
      >
        <Icon name="back" size={15} strokeWidth={2} />
        Advertising
      </button>

      <h1 className="m-0 font-display text-[22px] font-bold tracking-[-0.02em]">
        Sell a placement
      </h1>

      <div className="mt-4 space-y-3 rounded-card border border-line bg-white p-4 sm:p-5">
        <Input
          label="Pharmacy ID"
          mono
          value={pharmacyId}
          placeholder="12"
          hint="From the Pharmacies screen."
          error={error?.fieldErrors.pharmacy_id?.[0] ?? null}
          onChange={(event) => setPharmacyId(event.target.value)}
        />

        <label className="block">
          <span className="mb-1.5 block text-[12.5px] font-semibold text-ink">Where</span>
          <select
            value={placement}
            data-testid="deal-placement"
            onChange={(event) => change(setPlacement)(event.target.value)}
            className="min-h-11 w-full rounded-btn border border-line bg-white px-3 text-[14px] text-ink outline-none focus:border-blue"
          >
            {(rates.data?.placements ?? []).map((one) => (
              <option key={one.value} value={one.value}>
                {one.label} ({one.slots} slots)
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="mb-1.5 block text-[12.5px] font-semibold text-ink">Which shop</span>
          <select
            value={sector}
            data-testid="deal-sector"
            onChange={(event) => change(setSector)(event.target.value)}
            className="min-h-11 w-full rounded-btn border border-line bg-white px-3 text-[14px] text-ink outline-none focus:border-blue"
          >
            <option value="human">Pharmacy</option>
            <option value="veterinary">Veterinary</option>
          </select>
        </label>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1.5 block text-[12.5px] font-semibold text-ink">Priced per</span>
            <select
              value={unit}
              data-testid="deal-unit"
              onChange={(event) => change(setUnit)(event.target.value)}
              className="min-h-11 w-full rounded-btn border border-line bg-white px-3 text-[14px] text-ink outline-none focus:border-blue"
            >
              {(rates.data?.units ?? ['day', 'week', 'month', 'year']).map((one) => (
                <option key={one} value={one}>
                  {one}
                </option>
              ))}
            </select>
          </label>

          <Input
            label="How many"
            type="number"
            inputMode="numeric"
            min={1}
            value={units}
            onChange={(event) => change(setUnits)(event.target.value)}
          />
        </div>

        <Input
          label="Starts"
          type="date"
          value={startsOn}
          onChange={(event) => change(setStartsOn)(event.target.value)}
        />

        <Input
          label="Note (optional)"
          value={note}
          placeholder="Agreed with the owner on the phone, invoice to follow."
          onChange={(event) => setNote(event.target.value)}
        />
      </div>

      <Button
        variant="grey"
        className="mt-3"
        disabled={Number(units) < 1 || price.isPending}
        onClick={() => price.mutate()}
      >
        {price.isPending ? 'Pricing…' : 'Price it'}
      </Button>

      {quote ? (
        <div
          data-testid="deal-quote"
          className="mt-3 rounded-card border border-blue bg-blue-tint/40 px-4 py-3.5"
        >
          <p className="m-0 font-mono text-[20px] font-bold tabular-nums text-blue">
            {quote.total_display}
          </p>
          <p className="m-0 mt-0.5 text-[12.5px] leading-snug text-blue">
            {quote.units} × {quote.unit} at {quote.unit_price_display} ·{' '}
            {quote.starts_on} → {quote.ends_on}
          </p>
          <p className="m-0 mt-1 text-[11.5px] text-blue">
            {quote.already_sold} of {quote.slots} slots taken over those dates.
          </p>
        </div>
      ) : null}

      {error ? (
        <p
          role="alert"
          className="m-0 mt-3 rounded-card border border-danger bg-danger-tint px-3.5 py-2.5 text-[12.5px] font-medium text-danger"
        >
          {error.message}
        </p>
      ) : null}

      <div className="mt-5 flex flex-wrap gap-2">
        <Button
          disabled={pharmacyId === '' || quote === null || sell.isPending}
          onClick={() => sell.mutate()}
        >
          {sell.isPending ? 'Selling…' : 'Sell it'}
        </Button>
        <Button variant="grey" onClick={onDone}>
          Cancel
        </Button>
      </div>

      <p className="m-0 mt-3 text-[11.5px] leading-relaxed text-faint">
        It is created unpaid and shows to nobody until you mark it paid. A placement that went
        live the moment it was typed would mean anybody who can draft a deal can put a shop on the
        home screen for free.
      </p>
    </div>
  );
}

function Tile({ label, value, mono = false }: { label: string; value: string | null; mono?: boolean }) {
  return (
    <div className="rounded-card border border-line bg-white px-3.5 py-2">
      <p className="m-0 font-mono text-[10px] uppercase tracking-[0.1em] text-faint">{label}</p>
      {value === null ? (
        <Skeleton className="mt-1 h-5 w-16" />
      ) : (
        <p
          className={cn(
            'm-0 mt-0.5 font-bold tabular-nums text-ink',
            mono ? 'font-mono text-[15px]' : 'font-display text-[18px]',
          )}
        >
          {value}
        </p>
      )}
    </div>
  );
}
